import React, { useState, useRef, useEffect } from "react";
import "animate.css";
import { IMAGES_PATH } from "../../constants";
import { Box, Grid, IconButton, LinearProgress, Rating } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import CloseIcon from "@mui/icons-material/Close";
import { useAppDispatch } from "../../store";
import {
  fetchTypeformQuestionsForVendorFeedback,
  submitTypeformQuestionsForVendorFeedback,
  fetchTypeformQuestionsForVendorFeedbackStatus,
} from "../../api/typeform";
import { LoadingSpinner } from "../../components/loading-spinners";
import FlexBox from "../../components/layouts/Flexbox";
import Logo from "../../components/Logo";
import {
  TypeformFeedbackAlreadySubmitted,
  TypeformSubmitError,
  TypeformSubmitSuccess,
} from "./messages";
import { useParams } from "react-router";
import { useNavigate, useSearchParams } from "react-router-dom";

function getNote(type) {
  switch (type) {
    case "multi_select":
      return "Choose as many as you like";
    case "rating":
      return "1 = Bad, 3 = Average, 5 = Excellent";
    default:
      return "";
  }
}

export default function Typeform() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [nextQuestion, setNextQuestion] = useState(null);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState("");
  const [direction, setDirection] = useState("next");
  const [showNext, setShowNext] = useState(false);
  const questionRefs = useRef([]);
  const [questions, setQuestions] = useState([]);
  const dispatch = useAppDispatch();
  const [fetchingQuestions, setFetchingQuestions] = useState(true);
  const [showSubmitSuccess, setShowSubmitSuccess] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [feedbackSubmittedError, setFeedbackSubmittedError] = useState(false);
  const params = useParams();
  let [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const invitationId = params.id;
  const userId = searchParams.get("ambassador") || searchParams.get("vendor");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    getTypeformQuestionsForVendorFeedback();
  }, []);

  useEffect(() => {
    if (questionRefs.current[currentQuestion]) {
      questionRefs.current[currentQuestion].focus();
    }
  }, [currentQuestion]);

  useEffect(() => {
    if (nextQuestion !== null) {
      setShowNext(true);
    }
  }, [nextQuestion]);

  const getTypeformQuestionsForVendorFeedback = async () => {
    const checkFeedbackStatusResponse = await dispatch(
      fetchTypeformQuestionsForVendorFeedbackStatus({
        ambassadorInvitationId: invitationId,
        userID: userId,
      })
    );

    if (checkFeedbackStatusResponse?.success) {
      if (checkFeedbackStatusResponse?.data?.answers?.length) {
        setFetchingQuestions(false);
        return setFeedbackSubmittedError(true);
      }
    }

    const response = await dispatch(fetchTypeformQuestionsForVendorFeedback());

    if (response.success) {
      const modifiedQuestions = response?.data?.formData?.map((question) => {
        if (question.type === "multi_select") {
          return {
            ...question,
            customOption: "",
          };
        }

        return question;
      });
      setQuestions(modifiedQuestions);
    } else {
      setFeedbackError("Oops! Something went wrong");
    }
    setFetchingQuestions(false);
  };

  const validateCurrentQuestion = () => {
    const currentAnswer = answers[currentQuestion];

    if (questions[currentQuestion].type === "text" && !currentAnswer) {
      return "Please fill this in";
    }

    if (
      (questions[currentQuestion].type === "multi_select" ||
        questions[currentQuestion].type === "radio") &&
      (!currentAnswer || currentAnswer.length === 0)
    ) {
      return "Please make a selection";
    }

    if (questions[currentQuestion].type === "rating" && !currentAnswer) {
      return "Please make a selection";
    }

    if (questions[currentQuestion].type === "multi_select") {
      const currentAnswers = answers[currentQuestion] || [];

      if (
        currentAnswers?.includes("Other") &&
        !questions[currentQuestion]?.customOption
      ) {
        return "Please fill in the other option";
      }
    }

    return "";
  };

  const handleRemoveOther = (e, questionIndex) => {
    setQuestions((prev) => {
      const updatedQuestions = [...prev];
      updatedQuestions[questionIndex].customOption = "";
      return updatedQuestions;
    });

    const currentAnswers = answers[questionIndex] || [];

    const value = currentAnswers.filter((answer) => answer !== "Other");

    setAnswers((prev) => ({
      ...prev,
      [questionIndex]: value,
    }));
  };

  const handleNextQuestion = () => {
    const validationError = validateCurrentQuestion();

    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");

    if (isTransitioning) return;
    setIsTransitioning(true);
    setDirection("next");

    const nextIndex = currentQuestion + 1;
    if (nextIndex < questions.length) {
      setNextQuestion(nextIndex);
      setShowNext(true);

      setTimeout(() => {
        setCurrentQuestion(nextIndex);
        setNextQuestion(null);
        setIsTransitioning(false);
      }, 400);
    }
  };

  const handlePreviousQuestion = () => {
    setError("");
    if (isTransitioning || currentQuestion === 0) return;

    const prevIndex = currentQuestion - 1;
    setIsTransitioning(true);
    setDirection("previous");

    setShowNext(false);

    setTimeout(() => {
      setCurrentQuestion(prevIndex);
      setIsTransitioning(false);
    }, 400);
  };

  const handleSubmit = async () => {
    const validationError = validateCurrentQuestion();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    const userAnswers = questions.map((question, index) => {
      const answer = answers[index];

      if (question.type === "multi_select") {
        const a = {
          q: question.q,
          type: question.type,
          a: answer.join(", "),
        };

        if (answer.includes("Other")) {
          return {
            ...a,
            reason: question.customOption,
            a: answer.join(", "),
          };
        }

        return a;
      }

      if (question.type === "rating") {
        if (answer?.noRating) {
          return {
            q: question.q,
            a: null,
            reason: answer.reason,
            type: "rating",
          };
        } else {
          return {
            q: question.q,
            a: answer,
            type: "rating",
          };
        }
      }

      return {
        q: question.q,
        a: answer,
        type: question.type,
      };
    });

    const payload = {
      ambassadorInvitationId: invitationId,
      userId,
      answers: userAnswers,
    };

    const response = await dispatch(
      submitTypeformQuestionsForVendorFeedback(payload)
    );

    if (response.success) {
      setShowSubmitSuccess(true);
    } else {
      setFeedbackError("Oops! Something went wrong");
    }
    setIsSubmitting(false);
  };

  const handleChange = (e, questionIndex) => {
    const questionType = questions[questionIndex].type;
    let value;

    if (questionType === "multi_select" || questionType === "radio") {
      const { name, checked } = e.target;
      let currentAnswers = answers[questionIndex] || [];

      if (questionType === "radio") {
        value = e.target.name;
      } else {
        value = checked
          ? [...currentAnswers, name]
          : currentAnswers.filter((answer) => answer !== name);
      }
    } else {
      value = e.target.value;
    }

    setAnswers((prev) => ({
      ...prev,
      [questionIndex]: value,
    }));

    if (value && (questionType === "radio" || questionType === "rating")) {
      const nextIndex = currentQuestion + 1;
      if (nextIndex < questions.length) {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setError("");
        setDirection("next");
        setNextQuestion(nextIndex);
        setShowNext(true);

        setTimeout(() => {
          setCurrentQuestion(nextIndex);
          setNextQuestion(null);
          setIsTransitioning(false);
        }, 400);
      }
    }
  };

  const handleCustomInput = (e, questionIndex) => {
    setQuestions((prev) => {
      const updatedQuestions = [...prev];
      updatedQuestions[questionIndex].customOption = e.target.value;
      return updatedQuestions;
    });
  };

  const renderInputField = (question, index) => {
    switch (question.type) {
      case "text":
        return (
          <input
            type={question.inputType}
            placeholder={"Type your answer here..."}
            className="form-control"
            value={answers[index] || ""}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => e.key === "Enter" && handleNextQuestion()}
            ref={(el) => (questionRefs.current[index] = el)}
          />
        );
      case "radio":
      case "multi_select":
        return (
          <div className="custom-choose-wrapper">
            {question.options.map((option, i) => (
              <React.Fragment key={i}>
                <div className="custom-choose-control custom-other-control">
                  <input
                    type="checkbox"
                    id={`c-${i}`}
                    name={option}
                    checked={(answers[index] || [])?.includes(option)}
                    onChange={(e) => handleChange(e, index)}
                  />

                  <label htmlFor={`c-${i}`}>{option}</label>
                  {option === "Other" && (
                    <React.Fragment>
                      <input
                        type="text"
                        className="custom-other-text"
                        name={"choose-option"}
                        onChange={(e) => handleCustomInput(e, index)}
                        value={question?.customOption || ""}
                        style={{
                          display: (answers[index] || [])?.includes(option)
                            ? "block"
                            : "none",
                        }}
                        placeholder="Type Your Answer"
                      ></input>
                      {question?.customOption && (
                        <button
                          className="clear-btn"
                          onClick={(e) => handleRemoveOther(e, index)}
                        />
                      )}
                    </React.Fragment>
                  )}
                </div>
                <div className="custom-choose-control">
                  <input
                    type="text"
                    value={question?.customOption}
                    placeholder="Type your answer"
                    onChange={(e) => handleCustomInput(e, index)}
                  />
                </div>
              </React.Fragment>
            ))}
          </div>
        );
      case "rating":
        return (
          <>
            <div className="rating-control">
              <div style={{ marginBottom: "10px" }}>
                <Rating
                  name="size-large"
                  size="large"
                  sx={{ fontSize: 64 }}
                  onChange={(e) => handleChange(e, index)}
                  value={Number(answers[index]) || null}
                  emptyIcon={
                    <StarIcon sx={{ color: "white" }} fontSize="inherit" />
                  }
                  disabled={answers[index]?.noRating} 
                />
              </div>
            </div>
            <div style={{ marginLeft: "80px", marginTop: 20 }}>
              <label>
                <input
                  type="checkbox"
                  checked={answers[index]?.noRating || false}
                  onChange={(e) => handleNoRatingChange(e, index)}
                />
                Prefer not to rate
              </label>
            </div>
            {answers[index]?.noRating && (
              <div style={{ marginTop: 30 }}>
                <input
                  type="text"
                  placeholder="Please tell us why you don't want to rate"
                  className="form-control"
                  value={answers[index]?.reason || ""}
                  onChange={(e) => handleReasonChange(e, index)}
                />
              </div>
            )}
          </>
        );
      default:
        return null;
    }
  };

  const handleNoRatingChange = (e, questionIndex) => {
    const checked = e.target.checked;

    setAnswers((prev) => ({
      ...prev,
      [questionIndex]: {
        noRating: checked,
        rating: checked ? null : prev[questionIndex]?.rating,
      },
    }));

    setError("");
  };

  const handleReasonChange = (e, questionIndex) => {
    const value = e.target.value;

    setAnswers((prev) => ({
      ...prev,
      [questionIndex]: {
        ...prev[questionIndex],
        reason: value,
      },
    }));
  };

  const renderQuestion = (question, index, isNext = false) => (
    <div
      className={`cs-form-control ${
        direction === "next"
          ? isNext
            ? showNext
              ? "animate__animated animate__fadeInUp"
              : "animate__animated animate__fadeOutUp"
            : isTransitioning
            ? "animate__animated animate__fadeOutUp"
            : ""
          : isNext
          ? showNext
            ? "animate__animated animate__fadeInDown"
            : "animate__animated animate__fadeOutDown"
          : isTransitioning
          ? "animate__animated animate__fadeOutDown"
          : ""
      }`}
      key={index}
    >
      <label>
        <span className="question-number">{index + 1} →</span>
        {question.q}
      </label>
      {getNote(question?.type) && (
        <span className="note">{getNote(question?.type)}</span>
      )}
      {renderInputField(question, index)}
      <Grid container>
        <Grid item>{error && <span className="error-msg">{error}</span>}</Grid>
      </Grid>
      <div className="cs-control-btn">
        <button
          className="cs-btn"
          style={{
            marginTop: 20,
            backgroundColor: isSubmitting && "lightgray",
          }}
          onClick={
            currentQuestion === questions.length - 1
              ? handleSubmit
              : handleNextQuestion
          }
          disabled={isSubmitting}
        >
          {currentQuestion === questions.length - 1
            ? isSubmitting
              ? "Please wait..."
              : "Submit"
            : "Ok"}
        </button>
        {question.type === "text" &&
          currentQuestion !== questions.length - 1 && (
            <span>
              press <strong> Enter ↵</strong>
            </span>
          )}
      </div>
    </div>
  );

  const handleFeedbackMessageClose = () => {};
  const handleClose = () => {
    if (window?.history?.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/", { replace: true });
  };

  if (fetchingQuestions) {
    return <LoadingSpinner loading={fetchingQuestions} />;
  }

  if (showSubmitSuccess) {
    return (
      <TypeformSubmitSuccess
        onSuccessMessageClose={handleFeedbackMessageClose}
      />
    );
  }

  if (feedbackError || !userId || !invitationId) {
    return <TypeformSubmitError message={feedbackError} />;
  }

  if (feedbackSubmittedError) {
    return <TypeformFeedbackAlreadySubmitted />;
  }

  const totalQuestions = questions?.length;

  
  const progressPercentage = ((currentQuestion + 1) / totalQuestions) * 100;

  return (
    <>
      <FlexBox alignItems="center" justifyContent="space-between" p={2} gap={2}>
        <Logo style={{ width: 170 }} />
        <IconButton aria-label="Close" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </FlexBox>
      <Box sx={{ width: "100%" }}>
        <LinearProgress variant="determinate" value={progressPercentage} />
      </Box>
      <section className="custom-form-section">
        <div className="custom-form-block" style={{ marginTop: "10%" }}>
          <div className="cs-form-wrapper">
            <div className="cs-form-box">
              {renderQuestion(questions[currentQuestion], currentQuestion)}
              {nextQuestion !== null &&
                renderQuestion(questions[nextQuestion], nextQuestion, true)}
            </div>
          </div>
        </div>

        <div className="form-nav">
          {currentQuestion > 0 && (
            <button
              className="prev form-nav-btn"
              onClick={handlePreviousQuestion}
            >
              <img src={`${IMAGES_PATH}/angle_down_icon.svg`} />
            </button>
          )}
          {currentQuestion < questions.length - 1 && (
            <button className="next form-nav-btn" onClick={handleNextQuestion}>
              <img src={`${IMAGES_PATH}/angle_down_icon.svg`} />
            </button>
          )}
        </div>
      </section>
    </>
  );
}
